import { GetUserQuizQuestionPositionEnum, type UserQuizQuestionDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useApiClient } from '@/api/util/useApiClient.util.ts';
import i18n from '@/i18n.ts';
import { useUserContext } from '@/utils/providers/userContext/useUserContext.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

interface Params {
  userQuizId?: string;
}

type Response = UserQuizQuestionDto | undefined;

interface Return {
  isGettingQuizDetails: boolean;
  quizDetails: Response;
  quizDetailsError: AxiosError | null;
}

export const useGetQuizDetails = ({ userQuizId }: Params): Return => {
  const { restUserQuizApi } = useApiClient();
  const { t } = useTranslationWithPrefix('utils.errors');
  const { user } = useUserContext();
  const isQuizKnown = !!(user?.userId && userQuizId);
  // Set only for the fetch triggered by a language switch, which must not count as a quiz revisit
  const languageRefreshRef = useRef(false);
  const { data, error, isLoading, refetch } = useQuery<Response, AxiosError>({
    enabled: isQuizKnown,
    queryFn: async () => {
      const languageRefresh = languageRefreshRef.current;
      languageRefreshRef.current = false;
      if (!user?.userId || !userQuizId) {
        toast.error(t('unexpectedError'));
        throw new Error('Unexpected error - userId or userQuizId is not defined');
      }
      const { data } = await restUserQuizApi.getUserQuizQuestion(
        user.userId,
        userQuizId,
        GetUserQuizQuestionPositionEnum.Current,
        languageRefresh,
      );

      return data;
    },
    queryKey: [QueryKeyEnum.GET_QUIZ_QUESTION, user?.userId, userQuizId],
  });

  useEffect(() => {
    if (!isQuizKnown) {
      return;
    }
    // Same query key, so the question stays on screen (with the selected answers) until the translation arrives
    const refetchInNewLanguage = () => {
      languageRefreshRef.current = true;
      void refetch();
    };

    i18n.on('languageChanged', refetchInNewLanguage);
    return () => i18n.off('languageChanged', refetchInNewLanguage);
  }, [isQuizKnown, refetch]);

  return {
    isGettingQuizDetails: isLoading,
    quizDetails: data,
    quizDetailsError: error,
  };
};
